import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import App from './App.vue'
import { ContentService } from '@/services/ContentService'

// Import styles
import '../css/main.scss'

/**
 * Internationalization Setup
 * 
 * This application now fetches translations dynamically from Umbraco Dictionary Items
 * instead of having them hardcoded. To set up translations in Umbraco:
 * 
 * 1. Go to Settings > Dictionary in Umbraco backoffice
 * 2. Create dictionary items with the following structure:
 *    - navigation.home
 *    - navigation.skipToContent
 *    - navigation.menu
 *    - about.sectionLabel
 *    - about.defaultTitle
 *    - about.defaultDescription
 *    - about.imageAlt
 *    - about.metricsTitle
 *    - video.sectionLabel
 *    - video.defaultTitle
 *    - video.play
 *    - video.pause
 *    - video.mute
 *    - video.unmute
 *    - video.fullscreen
 *    - video.loading
 *    - video.error
 *    - video.notSupported
 *    - video.subtitles
 *    - video.progressLabel
 *    - video.retry
 *    - video.moreVideos
 *    - video.selectVideo
 *    - instagram.sectionLabel
 *    - instagram.defaultTitle
 *    - instagram.followUs
 *    - instagram.viewPost
 *    - instagram.loadMore
 *    - instagram.loading
 *    - instagram.viewAll
 *    - instagram.postImage
 *    - socialLinks.sectionLabel
 *    - socialLinks.defaultTitle
 *    - socialLinks.followOn
 *    - gameElements.sectionLabel
 *    - gameElements.defaultTitle
 *    - gameElements.learnMore
 *    - footer.sectionLabel
 *    - footer.defaultDescription
 *    - footer.newsletter.title
 *    - footer.newsletter.placeholder
 *    - footer.newsletter.inputLabel
 *    - footer.newsletter.submitLabel
 *    - footer.newsletter.submit
 *    - footer.newsletter.submitting
 *    - footer.quickLinks.title
 *    - footer.quickLinks.navLabel
 *    - footer.legal.title
 *    - labels.language
 * 
 * 3. Add translations for both English (en-US) and Dutch (nl-NL) languages
 * 4. The fallback translations in ContentService.getTranslations() will be used if
 *    the dictionary items are not set up yet
 */

// Detect browser's preferred language
const detectBrowserLanguage = (): string => {
  // Get browser languages in order of preference
  const browserLanguages = navigator.languages || [navigator.language]
  
  for (const lang of browserLanguages) {
    const normalized = lang.toLowerCase()
    
    // Check for Dutch
    if (normalized.startsWith('nl')) {
      return 'nl'
    }
    
    // Check for English
    if (normalized.startsWith('en')) {
      return 'en'
    }
  }
  
  // Default to English
  return 'en'
}

// Get the initial locale: priority is page language, then browser language, then default
const getInitialLocale = (): string => {
  // Check if we're on a page with a specific language in the HTML lang attribute
  const htmlLang = document.documentElement.lang
  if (htmlLang) {
    const normalized = htmlLang.toLowerCase()
    if (normalized.startsWith('nl')) return 'nl'
    if (normalized.startsWith('en')) return 'en'
  }
  
  // Fall back to browser language detection
  return detectBrowserLanguage()
}

const initialLocale = getInitialLocale()

// Create i18n instance with empty messages initially
const i18n = createI18n({
  locale: initialLocale,
  fallbackLocale: 'en',
  legacy: false, // Use Composition API mode
  messages: {} // Will be populated dynamically from Umbraco Dictionary Items
})

// Function to load translations for a specific locale
async function loadTranslations(_locale: string) {
  // Translations will be loaded on-demand from Umbraco when needed
  // For now, using empty messages with fallback system
  return null
}

// Function to initialize the application
async function initializeApp() {
  // Translations will load dynamically as needed

  // Create the main Vue application
  const app = createApp(App)

  // Install i18n
  app.use(i18n)

  // Provide ContentService globally
  app.provide('ContentService', ContentService)

  // Provide translation loader function
  app.provide('loadTranslations', loadTranslations)

  // Mount the app only if the #app element exists
  const appElement = document.getElementById('app')
  if (appElement) {
      app.mount('#app')
  } else {
      // Try to mount individual components
      await mountIndividualComponents()
  }
}

// Function to mount individual components to specific elements
async function mountIndividualComponents() {
  const { createApp } = await import('vue')
  
  // Mount Header component
  const headerElement = document.getElementById('vue-header')
  if (headerElement) {
    const { default: Header } = await import('./components/Header/Header.vue')
    
    const headerApp = createApp(Header)
    headerApp.use(i18n)
    headerApp.provide('ContentService', ContentService)
    headerApp.mount('#vue-header')
  }
  
  // Mount Hero component if placeholder exists
  const heroElement = document.getElementById('hero-section')
  if (heroElement) {
    const { default: Hero } = await import('./components/Hero/Hero.vue')

    const heroApp = createApp(Hero, {
      content: (window as any)?.umbracoPageData?.hero ?? null
    })
    heroApp.use(i18n)
    heroApp.provide('ContentService', ContentService)
    heroApp.mount('#hero-section')
  }
  
  // Mount Carousel component if placeholder exists
  const carouselElement = document.getElementById('carousel-section')
  if (carouselElement) {
    const { default: Carousel } = await import('./components/Carousel/Carousel.vue')

    const carouselApp = createApp(Carousel, {
      content: (window as any)?.umbracoPageData?.carousel ?? null
    })
    carouselApp.use(i18n)
    carouselApp.provide('ContentService', ContentService)
    carouselApp.mount('#carousel-section')
  }
  
  // Mount Social Links component
  const socialLinksElement = document.getElementById('social-links-section')
  if (socialLinksElement) {
    const currentCulture = document.documentElement.lang || 'en-us'
    
    try {
      const homepageData = await ContentService.getHomepage(currentCulture)
      
      const { default: SocialLinks } = await import('./components/SocialLinks.vue')
      
      // API returns: { socialLinks: { title, links: [] } }
      // Component expects props: { content: { socialLinksTitle, socialLinks: [] } }
      const socialLinksData = {
        socialLinksTitle: homepageData?.socialLinks?.title || '',
        socialLinks: homepageData?.socialLinks?.links || []
      }
      
      const socialLinksApp = createApp(SocialLinks, {
        content: socialLinksData
      })
      socialLinksApp.use(i18n)
      socialLinksApp.provide('ContentService', ContentService)
      socialLinksApp.mount('#social-links-section')
    } catch (error) {
      console.error('[SocialLinks] Error loading data:', error)
    }
  }
  
  // Mount other components as needed
  // Add more component mounts here if you have other specific elements
}

// Initialize the application
initializeApp().catch(error => {
  console.error('Failed to initialize application:', error)
})
